import React from 'react'
import PropTypes from 'prop-types'
import { startOfHour, addHours, endOfDay, format, getTime, isValid, isEqual, set } from 'date-fns'
import {
  Icon,
  Text,
  Stack,
  Button,
  Portal,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  useFormControlContext,
  useControllableState,
  useDisclosure,
  useId,
} from '@chakra-ui/react'
import FiChevronUp from '@meronex/icons/fi/FiChevronUp'
import FiChevronDown from '@meronex/icons/fi/FiChevronDown'

import { Icons } from '../../icons'
import { dateHelper } from '../../utils'

const defaultFormatAdapter = {
  input: (time) => format(time, 'h:mm a'),
  label: (time) => format(time, 'h:mm a'),
}

export const TimePicker = ({
  label,
  placeholder,
  colorScheme,
  step,
  includeEnd,
  usePortal,
  startHour,
  endHour,
  defaultValue,
  value: valueProp,
  onChange,
  isDisabled,
  formatAdapter: formatAdapterProp,
  minWidth,
  height,
  buttonProps,
  listProps,
}) => {
  const id = useId()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const field = useFormControlContext()
  const [value, setValue] = useControllableState({ defaultValue, value: valueProp, onChange })
  const formatAdapter = React.useMemo(() => ({ ...defaultFormatAdapter, ...formatAdapterProp }), [formatAdapterProp])

  const options = React.useMemo(() => {
    const start = set(isValid(value) ? value : new Date(), { hours: startHour, minutes: 0, seconds: 0, milliseconds: 0 })
    const end = endHour === 24 ? set(endOfDay(value), { milliseconds: 0 }) : set(value, { hours: endHour, minutes: 0, seconds: 0, milliseconds: 0 })
    return dateHelper.eachMinuteOfInterval({ start, end }, { step, includeEnd }).map((time) => ({ label: formatAdapter.label(time), value: time }))
  }, [value, startHour, endHour, step, includeEnd, formatAdapter])
  const selectedOption = React.useMemo(() => options.find((option) => isValid(value) && isEqual(option.value, value)), [options, value])
  const hasValue = React.useMemo(() => isValid(selectedOption?.value), [selectedOption])

  const onOptionGroupChange = React.useCallback((value) => setValue(new Date(Number(value))), [setValue])

  React.useEffect(() => {
    if (!isOpen) return
    setTimeout(() => document.querySelector(`[data-id="${id}"][data-date-picker="${getTime(value)}"]`).scrollIntoView(), 0)
  }, [isOpen, id, value])

  return (
    <Menu placement='bottom' matchWidth isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <MenuButton
        as={Button}
        minWidth={minWidth}
        height={height}
        variant='outline'
        leftIcon={<Icon as={Icons.Clock} boxSize='1rem' color='gray.500' />}
        rightIcon={<Icon as={isOpen ? FiChevronUp : FiChevronDown} boxSize='1rem' />}
        color={hasValue ? 'gray.800' : 'gray.600'}
        fontSize='sm'
        borderColor='gray.200'
        bgColor='white'
        isDisabled={isDisabled}
        _invalid={{ borderColor: 'red.500' }}
        _active={{ borderColor: `${colorScheme}.500`, bgColor: 'gray.100', shadow: `outline-${colorScheme}` }}
        _selected={{ borderColor: `${colorScheme}.500`, shadow: `outline-${colorScheme}` }}
        _disabled={{ cursor: 'not-allowed', color: 'gray.700', bgColor: 'gray.50' }}
        _hover={{ bgColor: 'gray.100', _disabled: { bgColor: 'gray.50' } }}
        {...(field?.isInvalid ? { 'data-invalid': true } : {})}
        {...buttonProps}
      >
        <Stack direction='row'>
          {label && <Text color='gray.600'>{label}</Text>}
          <Text>{hasValue ? formatAdapter.input(selectedOption.value) : placeholder}</Text>
        </Stack>
      </MenuButton>

      {React.createElement(
        usePortal ? Portal : React.Fragment,
        null,
        <MenuList paddingY='0.5rem' maxHeight='12.5rem' overflow='auto' {...listProps}>
          {/* value accept string type only */}
          {/* use number type value to guarantee correctness of time */}
          <MenuOptionGroup type='radio' value={hasValue ? String(getTime(value)) : undefined} onChange={onOptionGroupChange}>
            {options.map((option, index) => {
              const stringValue = String(getTime(option.value))
              return (
                <MenuItemOption
                  key={index}
                  data-id={id}
                  data-date-picker={stringValue}
                  paddingY='0.75rem'
                  lineHeight='1.25'
                  fontSize='sm'
                  value={stringValue}
                >
                  {option.label}
                </MenuItemOption>
              )
            })}
          </MenuOptionGroup>
        </MenuList>,
      )}
    </Menu>
  )
}

TimePicker.propTypes = {
  label: PropTypes.node,
  placeholder: PropTypes.node,
  colorScheme: PropTypes.string,
  step: PropTypes.number,
  includeEnd: PropTypes.bool,
  usePortal: PropTypes.bool,
  startHour: PropTypes.number,
  endHour: PropTypes.number,
  defaultValue: PropTypes.instanceOf(Date),
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  formatAdapter: PropTypes.shape({
    input: PropTypes.func,
    label: PropTypes.func,
  }),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  buttonProps: PropTypes.object,
  listProps: PropTypes.object,
}
TimePicker.defaultProps = {
  colorScheme: 'brand',
  step: 30,
  includeEnd: false,
  usePortal: true,
  startHour: 0,
  endHour: 24,
  defaultValue: addHours(startOfHour(new Date()), 1),
  minWidth: '17.5rem',
  height: '3.5rem',
  buttonProps: {},
  listProps: {},
}
